/*
 * Special Module for handling the Topics, Topic, and Standard Response pages, and switching between them without distroying each other
 * This module basically is just a wrapper for the Topics, Topic, and Standard Response modules, but handles some functionality for
 * switching between them
 */

define([
  "marionette",
  "backbone",
  "jquery",
  "configuration/properties",
  "messaging/messenger",
  "./topicsSplitView",
  "./topics/topicsModule",
  "./topic/topicModule",
  "./standardResponse/standardResponseModule",
  "./miniLearner/miniLearnerModule",
  "./variablePrompt/variablePromptModule",
  "./snippet/snippetModule",
  "./topics/topicsModel",
  "./snippet/snippetModel",
], function (
  Marionette,
  Backbone,
  $,
  Properties,
  Messenger,
  TopicsSplitView,
  TopicsModule,
  TopicModule,
  StandardResponseModule,
  MiniLearnerModule,
  VariablePromptModule,
  SnippetModule,
  TopicsModel,
) {
  const Router = Marionette.AppRouter.extend({
    appRoutes: {
      ":kbId/topics(/new)": "_routeTopics",
      ":kbId/topic/:intentId": "_routeTopic",
      ":kbId/snippet/new": "_routeTopics",
      ":kbId/snippet/:snippetId": "_routeSnippet",
      ":kbId/standard/:transId": "_routeStandardResponse",
      ":kbId/prompt/:variableId": "_routeVariablePrompt",
    },
  });
  return Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "topicsSplit";
      this.region = options.region;
      this.rootTopicGroup = null; //let the top level module manage the root topic group
      //We cannot set up decision topics here because there is no KB - the topic split module is loaded
      //right away, before the user logs in and thus before there is an active KB.
      //Instead, they are loaded on the kb:initial:set reqres handler below.
      this._setUpVents();
      this._setUpReqRes();
      this.router = new Router({
        controller: this,
      });
    },
    show: function () {
      //load the root topic group
      this.rootTopicGroup = Messenger.reqres.request("root:topic:group");
      this.rootSnippetGroup = Messenger.reqres.request("root:snippet:group");
      this._refreshKbData();
      this.topicsLoaded = false; //true if we have shown the topics page before
      this.layout = new TopicsSplitView.topicsSplitLayout();
      this.topics = this.addSubModule(
        new TopicsModule({
          region: this.layout.topicsRegion,
          rootTopicGroup: this.rootTopicGroup,
          rootSnippetGroup: this.rootSnippetGroup,
        }),
      );
      this._closeTopic();
      this._closeSnippet();
      this._closeStandardResponse();
      this._closeVariablePrompt();
      this.miniLearner = this.addSubModule(
        new MiniLearnerModule({ region: this.layout.miniLearner }),
      );
      this.showRegion(this.layout);

      //if we are supposed to show the mini learner, do it now
      if (this.showMiniLearner) {
        this.miniLearner.show(false); //show it collapsed
      }
    },
    _refreshKbData: function () {
      // refresh some KB data when navigating to topics page. This can be helpful when user has
      // modified KB data that is needed in topic but is not changed often. This avoids fetching
      // fresh data everytime a topic is opened.
      if (Messenger.reqres.request("user:is:admin")) {
        Messenger.reqres.request("fetch:kb:campaignsWithDefault");
      }
    },
    showTopic: function (topic, intentId) {
      this._closeStandardResponse();
      this._closeVariablePrompt();
      this._closeSnippet();
      Messenger.command.execute("change:menu", "topics");
      //set URL
      const kbId = Messenger.reqres.request("active:kb");
      if (topic.isNew()) {
        //simply use the topics url because the topic does not have an id
        Backbone.history.navigate(kbId + "/topics/new");
      } else {
        Backbone.history.navigate(kbId + "/topic/" + intentId); //use the separate intentId to fill out URL
      }

      if (!this.isOpen) {
        //initialize region if we haven't done so already
        this.show();
      }
      this.topic = this.addSubModule(
        new TopicModule({ region: this.layout.topicRegion, rootTopicGroup: this.rootTopicGroup }),
      );

      //set save handler
      Messenger.onSave(() => {
        this.topic.save();
      });
      Messenger.onSaveConflict(() => {
        // It is impossible to "fix" a 409 Conflict error, so redirect to the Topics List.
        // Make sure all modal dialogs are closed before showing the topics list.
        Messenger.vent.trigger("close:modal");
        this.showTopics();
      });

      //hide topics page
      this.layout.hideTopics();
      //unhide topic page
      this.layout.showTopic();
      //make sure main region is visible
      this.region.trigger("show");

      //toggle isOpen parameter
      this.topics.isOpen = false;
      //show topic page (always get a fresh page)
      this.topic.show(topic);
    },
    showTopics: function () {
      Messenger.command.execute("change:menu", "topics");
      //set URL
      const kbId = Messenger.reqres.request("active:kb");
      //if the hash has "/new" at the end, then replace the existing history entry
      const endsWithNew = window.location.hash.slice(-4) === "/new";
      Backbone.history.navigate(kbId + "/topics", { replace: endsWithNew });
      document.title = $.t("menuBar.topics") + " - " + Properties.applicationName;
      //hide the context menu
      Messenger.vent.trigger("hide:context:menu");
      if (!this.isOpen) {
        this.show();
      }

      //set save handler
      Messenger.onSave(function () {
        this.topics.save();
      }, this);

      //hide topic page
      this.layout.hideTopic();
      //unhide topics page
      this.layout.showTopics();
      //make sure main region is visible
      this.region.trigger("show");

      this._closeTopic();
      this._closeSnippet();
      this._closeStandardResponse();
      this._closeVariablePrompt();
      const isSnippetOpen = this.snippetIsOpen;
      this.snippetIsOpen = false;
      if (!this.topicsLoaded) {
        this.topicsLoaded = true;
        this.topics.show(isSnippetOpen);
      } else {
        this.topics.isOpen = true; //toggle isOpen parameter
        this.topics.reloadData(isSnippetOpen);
      }
    },
    showStandardResponse: function (standardResponse, transId) {
      this._closeSnippet();
      this._closeTopic();
      this._closeVariablePrompt();
      Messenger.command.execute("change:menu", "topics");
      //set URL
      const kbId = Messenger.reqres.request("active:kb");
      Backbone.history.navigate(kbId + "/standard/" + transId);

      if (!this.isOpen) {
        //initialize region if we haven't done so already
        this.show();
      }
      this.standardResponse = this.addSubModule(
        new StandardResponseModule({ region: this.layout.topicRegion }),
      );

      //set save handler
      Messenger.onSave(function () {
        this.standardResponse.save();
      }, this);

      //hide topics page
      this.layout.hideTopics();
      //unhide topic page
      this.layout.showTopic();

      //toggle isOpen parameter
      this.topics.isOpen = false;
      //show topic page (always get a fresh page)
      this.standardResponse.show(standardResponse);
    },
    showVariablePrompt: function (variable, variableId) {
      this._closeSnippet();
      this._closeTopic();
      this._closeStandardResponse();
      Messenger.command.execute("change:menu", "topics");
      //set URL
      const kbId = Messenger.reqres.request("active:kb");
      Backbone.history.navigate(kbId + "/prompt/" + variableId);

      if (!this.isOpen) {
        //initialize region if we haven't done so already
        this.show();
      }
      this.variablePrompt = this.addSubModule(
        new VariablePromptModule({ region: this.layout.topicRegion }),
      );

      //set save handler
      Messenger.onSave(function () {
        this.variablePrompt.save();
      }, this);

      //hide topics page
      this.layout.hideTopics();
      //unhide topic page
      this.layout.showTopic();

      //toggle isOpen parameter
      this.topics.isOpen = false;
      //show prompt page (always get a fresh page)
      this.variablePrompt.show(variable);
    },
    showSnippet: function (snippetOrSnippetId) {
      if (typeof snippetOrSnippetId == "string" || typeof snippetOrSnippetId == "number") {
        const snippet = Messenger.reqres.request("fetch:snippet", snippetOrSnippetId);
        snippet.onSynced(() => this.showSnippet(snippet));
        return;
      }
      const snippet = snippetOrSnippetId;
      this._closeTopic();
      this._closeStandardResponse();
      this._closeVariablePrompt();
      Messenger.command.execute("change:menu", "topics");
      //set URL
      const kbId = Messenger.reqres.request("active:kb");
      if (snippet.isNew()) {
        Backbone.history.navigate(kbId + "/snippet/new");
      } else {
        Backbone.history.navigate(kbId + "/snippet/" + snippet.id);
      }

      if (!this.isOpen) {
        //initialize region if we haven't done so already
        this.show();
      }
      this.snippet = this.addSubModule(new SnippetModule({ region: this.layout.topicRegion }));

      //set save handler
      Messenger.onSave(() => {
        this.snippet.save();
      });

      //hide topics page
      this.layout.hideTopics();
      //unhide topic page
      this.layout.showTopic();

      //toggle isOpen parameter
      this.topics.isOpen = false;
      this.snippetIsOpen = true;
      //show prompt page (always get a fresh page)
      this.snippet.show(snippet);
    },
    _closeStandardResponse: function () {
      if (this.standardResponse) {
        const index = this.subModules.indexOf(this.standardResponse);
        if (index >= 0) {
          this.subModules.splice(index, 1);
        }
        this._closeSubModule(this.standardResponse);
        this.standardResponse = null;
      }
    },
    _closeSnippet: function () {
      if (this.snippet) {
        const index = this.subModules.indexOf(this.snippet);
        if (index >= 0) {
          this.subModules.splice(index, 1);
        }
        this._closeSubModule(this.snippet);
        this.snippet = null;
      }
    },
    _closeTopic: function () {
      if (this.topic) {
        const index = this.subModules.indexOf(this.topic);
        if (index >= 0) {
          this.subModules.splice(index, 1);
        }
        this._closeSubModule(this.topic);
        this.topic = null;
      }
    },
    _closeVariablePrompt: function () {
      if (this.variablePrompt) {
        const index = this.subModules.indexOf(this.variablePrompt);
        if (index >= 0) {
          this.subModules.splice(index, 1);
        }
        this._closeSubModule(this.variablePrompt);
        this.variablePrompt = null;
      }
    },
    _routeTopic: function (kbId, intentId) {
      //set active kb
      Messenger.command.execute("set:active:kb", kbId);
      const topic = Messenger.reqres.request("topic", intentId); //load topic from server
      topic.onSynced(() => {
        if (topic.get("archived")) {
          Backbone.history.navigate(kbId + "/topics", { trigger: true });
          Messenger.okDialog("topic.archived.openError");
          return;
        }
        Messenger.command.execute("change:menu", "topics");
        this.showTopic(topic, intentId);
      });
    },
    _routeSnippet: function (kbId, snippetId) {
      Messenger.command.execute("set:active:kb", kbId);
      this.showSnippet(snippetId);
    },
    _routeTopics: function (kbId) {
      const isAuthor = Messenger.reqres.request("user:is:author", kbId);
      if (!isAuthor) {
        Backbone.history.navigate("", { trigger: true });
        return;
      }
      //set active kb
      Messenger.command.execute("set:active:kb", kbId);
      Messenger.command.execute("change:menu", "topics");
      this.showTopics();
    },
    _routeStandardResponse: function (kbId, transId) {
      const isAuthor = Messenger.reqres.request("user:is:author", kbId);
      if (!isAuthor) {
        Backbone.history.navigate("", { trigger: true });
        return;
      }
      //set active kb
      Messenger.command.execute("set:active:kb", kbId);
      const standardResponse = Messenger.reqres.request("standard:response", transId);
      this.showStandardResponse(standardResponse, transId);
    },
    _routeVariablePrompt: function (kbId, variableId) {
      const isAuthor = Messenger.reqres.request("user:is:author", kbId);
      if (!isAuthor) {
        Backbone.history.navigate("", { trigger: true });
        return;
      }
      //set active kb
      Messenger.command.execute("set:active:kb", kbId);
      const variable = Messenger.reqres.request("conversation:variable", variableId);
      this.showVariablePrompt(variable, variableId);
    },
    _setUpVents: function () {
      Messenger.vent.on(
        "show:topics",
        function () {
          this.showTopics();
        },
        this,
      );

      Messenger.vent.on(
        "show:topic",
        function (topic) {
          if (topic.get("archived")) {
            Messenger.okDialog("topic.archived.openError");
          } else if (!topic.isNew()) {
            this._routeTopic(Messenger.reqres.request("active:kb"), topic.get("intentId"));
          } else {
            this.showTopic(topic, topic.get("intentId"));
          }
        },
        this,
      );

      Messenger.vent.on(
        "show:standard:response",
        function (standardResponse) {
          this.showStandardResponse(standardResponse, standardResponse.get("intentId"));
        },
        this,
      );

      Messenger.vent.on(
        "show:variable:prompt",
        (variable) => {
          this.showVariablePrompt(variable, variable.id);
        },
        this,
      );

      Messenger.vent.on(
        "show:snippet:edit:page",
        (snippetOrSnippetId) => this.showSnippet(snippetOrSnippetId),
        this,
      );

      //Call to show the mini-learner and whether it should be expanded or not.
      Messenger.vent.on(
        "show:mini:learner",
        function (expand) {
          //if this module is open, then show the mini learner.  Otherwise, just make sure that it shows later
          if (this.isOpen) {
            this.miniLearner.show(expand);
          }
          this.showMiniLearner = true;
        },
        this,
      );

      /*
       * Vent for adding a new topic from a learner transcript.  We need to have the vent here because it may
       * be called before the topicsModule is even initialized.  This is how this call differs from the "add:topic"
       * vent in TopicsModule.  To do this, we need to show split module, if it isn't already open.  This will
       * initialize the topic and topics modules.  Next we will add a new topic using the topics module.
       * Note that this vent is also used to create a new topic from the dashboard.  In that case, a transcript is
       * not passed.
       */
      Messenger.vent.on(
        "add:learner:topic",
        function (transcript, localeId) {
          if (!this.isOpen) {
            //initialize region if we haven't done so already (will also load rootTopicGroup)
            this.show();
          }
          this.rootTopicGroup.onSynced(function () {
            const newTopic = this.rootTopicGroup.addTopic();
            //show the topic page for the new topic
            this.showTopic(newTopic, newTopic.get("intentId"));
            //if we are passed a transcript, apply it to the topic
            if (transcript) {
              //fire an event for the topic module to handle applying the transcript to the topic
              Messenger.vent.trigger("apply:transcript:to:topic", transcript, localeId);
            }
          }, this);
        },
        this,
      );

      //A catch for when we change kbs.  We need to make sure the mini learner is disabled again or else we could get a
      //missing reqres error
      Messenger.vent.on(
        "kb:changed",
        function () {
          this.showMiniLearner = false;
          if (
            Messenger.reqres.request("user:is:author") ||
            Messenger.reqres.request("user:is:admin")
          ) {
            this.decisionTopics = new TopicsModel.decisionTopics(); //A collection of topics forming the decision choices for this KB
          }
        },
        this,
      );

      Messenger.command.setHandler(
        "load:kb:decision:topics",
        function () {
          if (
            Messenger.reqres.request("user:is:author") ||
            Messenger.reqres.request("user:is:admin")
          ) {
            this.decisionTopics = new TopicsModel.decisionTopics(); //A collection of topics forming the decision choices for this KB
          }
        },
        this,
      );
    },
    _setUpReqRes: function () {
      Messenger.reqres.setHandler(
        "topic:decisions",
        function () {
          return this.decisionTopics;
        },
        this,
      );
      Messenger.reqres.setHandler(
        "topic",
        (intentId) => {
          // Always load the Topic from the server so that the latest information is available.
          // This is critical for optimistic locking to work correctly
          return Messenger.reqres.request("fetch:topic", intentId);
        },
        this,
      );
      Messenger.reqres.setHandler(
        "snippet",
        (snippetId) => {
          return Messenger.reqres.request("fetch:snippet", snippetId);
        },
        this,
      );
    },
  });
});
